import type { CalculatedAttributeResolver } from '@hyphengroup/cms-components/calculatedAttributes/resolve';
import {
  getProductGridCardMetrics,
  getProductGridCardPreviewItems,
} from '@hyphengroup/cms-components/components/ProductGridCard';
import type { ProductGridCardProps } from '@hyphengroup/cms-components/components/ProductGridCard';
import {
  getRewardCampaignId,
  getRewardCampaignName,
} from '@hyphengroup/cms-components/utils/analytics';
import { resolveProductTag } from '@hyphengroup/cms-components/utils/helpers';
import type { ProductCardGridTrackingPayload } from './types';
import type { ComponentProductCardGridItem } from '$/(cms)/_modules/cmsService/types';

type CardDefaults = {
  applyCtaLabel?: string;
  viewDetailsCtaLabel?: string;
};

type CardOverrides = {
  applyCtaUrl?: string;
  onPrimaryCtaClick?: ProductGridCardProps['onPrimaryCtaClick'];
  viewDetailsCtaUrl?: string;
  onSecondaryCtaClick?: ProductGridCardProps['onSecondaryCtaClick'];
};

export const buildBaseTrackingPayload = ({
  event,
  componentTitle,
  index,
  pathname,
  productId,
  productName,
  providerName,
  rewardCampaignId,
  rewardCampaignName,
}: {
  event: ProductCardGridTrackingPayload['event'];
  componentTitle: string;
  index: number;
  pathname: string;
  productId: string;
  productName: string;
  providerName: string;
  rewardCampaignId?: string;
  rewardCampaignName?: string;
}): ProductCardGridTrackingPayload => ({
  event,
  action: 'click',
  component_type: 'product-card',
  component_title: componentTitle,
  item_list_name: pathname,
  items: [
    {
      item_id: productId,
      item_name: productName,
      item_brand: providerName,
      index: String(index),
    },
  ],
  reward_campaign_name: rewardCampaignName,
  reward_campaign_id: rewardCampaignId,
});

export const getProductCardGridRewardCampaign = (
  item: ComponentProductCardGridItem,
) => {
  const exclusiveAndBundleOffers = [
    ...(item.product?.exclusive_offers ?? []),
    ...(item.product?.bundle_exclusive_offers ?? []),
  ];

  return {
    rewardCampaignName: getRewardCampaignName(exclusiveAndBundleOffers),
    rewardCampaignId: getRewardCampaignId(exclusiveAndBundleOffers),
  };
};

const normalizeUrl = (url?: string): string | undefined => {
  if (!url) {
    return undefined;
  }

  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  return url.startsWith('/') ? url : `/${url}`;
};

export const mapProductCardGridItemToCardProps = (
  item: ComponentProductCardGridItem,
  defaults: CardDefaults = {},
  overrides: CardOverrides = {},
  /** The engine facade — see `getProductGridCardMetrics` (CMS-1890). */
  resolveCalculated?: CalculatedAttributeResolver,
): ProductGridCardProps => {
  const { metricPrefix, metricPrimary, metricSecondary } =
    getProductGridCardMetrics({
      product: item.product,
      highlightTextAttribute: item.highlightTextAttribute,
      highlightTextOverride: item.highlightTextOverride,
      resolveCalculated,
    });
  const previewItems = getProductGridCardPreviewItems({
    product: item.product,
    orderByOffer: true,
  });
  const viewDetailsUrl = normalizeUrl(overrides.viewDetailsCtaUrl);
  const applyUrl = normalizeUrl(overrides.applyCtaUrl);

  return {
    id: item.id,
    tagColor: item.colorTag,
    tagText: resolveProductTag({
      product: item.product,
      pageLevelTag: item.productTagOverride,
    }),
    imageUrl:
      item.product?.product_image || item.product?.provider?.provider_image,
    imageAlt: item.product?.product_name || 'Product image',
    title: item.product?.product_name,
    metricPrefix,
    metricPrimary,
    metricSecondary,
    previewItems,
    previewStyle: 'card',
    primaryCtaLabel: item.applyCtaLabel || defaults.applyCtaLabel,
    primaryCtaUrl: applyUrl,
    onPrimaryCtaClick: overrides.onPrimaryCtaClick,
    primaryCtaPosition: 'afterImage',
    primaryCtaIcon: 'arrow',
    secondaryCtaLabel:
      viewDetailsUrl || overrides.onSecondaryCtaClick
        ? item.viewDetailsCtaLabel || defaults.viewDetailsCtaLabel
        : undefined,
    secondaryCtaUrl: viewDetailsUrl,
    onSecondaryCtaClick: overrides.onSecondaryCtaClick,
  };
};
